import { CELL_TYPES, createCell, uid, cssToStyles, createEmptySchema } from './cellSchema';

const extractStyles = (el) => {
  if (!el || !el.style) return {};
  const styles = {};
  const computed = el.getAttribute('style') || '';
  return cssToStyles(computed);
};

const getDimensions = (el, defaults = { width: 200, height: 100 }) => {
  const styles = extractStyles(el);
  return {
    width: parseInt(styles.width, 10) || defaults.width,
    height: parseInt(styles.height, 10) || defaults.height,
  };
};

const isButtonElement = (el) => {
  if (el.dataset?.comp === 'button') return true;
  if (el.classList?.contains('q-button')) return true;
  if (el.classList?.contains('q-embed-button')) return true;

  const button = el.querySelector('button');
  if (button) return true;
  if (el.tagName === 'BUTTON') return true;

  const link = el.querySelector('a');
  if (link) {
    if (link.querySelector('button')) return true;

    const styles = extractStyles(link);
    const hasButtonStyles =
      styles.backgroundColor &&
      styles.backgroundColor !== 'transparent' &&
      styles.padding &&
      (styles.borderRadius || styles.display === 'inline-block');
    if (hasButtonStyles) return true;
  }

  return false;
};

const isDividerElement = (el) => {
  if (el.dataset?.comp === 'divider') return true;
  if (el.classList?.contains('q-divider')) return true;
  if (el.classList?.contains('q-embed-divider')) return true;

  if (el.tagName === 'HR') return true;

  const firstChild = el.firstElementChild;
  if (firstChild?.dataset?.comp === 'divider') return true;

  const styles = extractStyles(el);
  if (styles.borderTop && el.textContent?.trim() === '') return true;

  if (firstChild) {
    const childStyles = extractStyles(firstChild);
    if (childStyles.borderTop && firstChild.textContent?.trim() === '') return true;
  }

  return false;
};

const isSocialElement = (el) => {
  if (el.dataset?.comp === 'social') return true;
  if (el.classList?.contains('q-social')) return true;
  if (el.classList?.contains('q-embed-social')) return true;

  const firstChild = el.firstElementChild;
  if (firstChild?.dataset?.comp === 'social') return true;

  const links = el.querySelectorAll('a');
  let socialCount = 0;
  links.forEach((link) => {
    const href = link.href?.toLowerCase() || '';
    if (
      href.includes('linkedin') ||
      href.includes('facebook') ||
      href.includes('twitter') ||
      href.includes('instagram') ||
      href.includes('youtube')
    ) {
      socialCount++;
    }
  });
  return socialCount >= 2;
};

const isImageElement = (el) => {
  if (el.tagName === 'IMG') return true;
  if (el.querySelector('img')) return true;
  return false;
};

const parseButton = (el, x, y) => {
  const button = el.querySelector('button');
  const link = el.querySelector('a') || el;
  const styledElement = button || link;

  const text = (button?.textContent || link.textContent)?.trim() || 'Button';
  const url = link.href || '';

  const styles = extractStyles(styledElement);
  const containerStyles = extractStyles(el);

  let borderColor = 'transparent';
  if (styles.border && styles.border !== 'none') {
    const borderMatch = styles.border.match(/(\d+)px\s+solid\s+(.*)/);
    if (borderMatch) {
      borderColor = borderMatch[2];
    }
  }

  const btnWidthAttr = el.getAttribute?.('data-btn-width') || el.closest?.('[data-btn-width]')?.getAttribute('data-btn-width');
  const btnHeightAttr = el.getAttribute?.('data-btn-height') || el.closest?.('[data-btn-height]')?.getAttribute('data-btn-height');
  const parsedBtnWidth = btnWidthAttr ? parseInt(btnWidthAttr, 10) : (parseInt(styles.minWidth, 10) || 40);
  const parsedBtnHeight = btnHeightAttr ? parseInt(btnHeightAttr, 10) : (parseInt(styles.minHeight, 10) || parseInt(styles.height, 10) || 15);

  return createCell(CELL_TYPES.BUTTON, {
    x,
    y,
    content: text,
    buttonUrl: url,
    buttonWidth: parsedBtnWidth,
    buttonHeight: parsedBtnHeight,
    width: parseInt(styles.width, 10) || 150,
    height: parseInt(styles.height, 10) || 50,
    styles: {
      backgroundColor:
        styles.backgroundColor || el.style?.getPropertyValue?.('--btn-bg') || '#7a6a55',
      color: styles.color || el.style?.getPropertyValue?.('--btn-color') || '#ffffff',
      borderRadius: styles.borderRadius || el.style?.getPropertyValue?.('--btn-radius') || '10px',
      padding: styles.padding || '12px 20px',
      textAlign: containerStyles.textAlign || 'center',
      fontWeight: styles.fontWeight || '600',
      borderColor: borderColor,
    },
  });
};

const parseDivider = (el, x, y) => {
  const dividerEl =
    el.dataset?.comp === 'divider'
      ? el
      : el.firstElementChild?.dataset?.comp === 'divider'
        ? el.firstElementChild
        : el;
  const line = dividerEl.querySelector('.q-divider-line') || dividerEl;
  const styles = extractStyles(line);

  let thickness = 2;
  let color = '#7a6a55';

  if (dividerEl.dataset?.thickness) {
    thickness = parseInt(dividerEl.dataset.thickness, 10) || 2;
  }
  if (dividerEl.dataset?.color) {
    color = dividerEl.dataset.color;
  }

  if (styles.borderTop && !dividerEl.dataset?.thickness) {
    const match = styles.borderTop.match(/(\d+)px\s+solid\s+(.*)/);
    if (match) {
      thickness = parseInt(match[1], 10);
      color = match[2];
    }
  }

  return createCell(CELL_TYPES.DIVIDER, {
    x,
    y,
    width: 400,
    height: 20,
    dividerColor: color,
    dividerThickness: thickness,
  });
};

const parseSocial = (el, x, y) => {
  const socialEl =
    el.dataset?.comp === 'social'
      ? el
      : el.firstElementChild?.dataset?.comp === 'social'
        ? el.firstElementChild
        : el;

  let socialLinks = {
    linkedin: socialEl.dataset?.linkedin || '',
    facebook: socialEl.dataset?.facebook || '',
    twitter: socialEl.dataset?.twitter || '',
    instagram: socialEl.dataset?.instagram || '',
    youtube: socialEl.dataset?.youtube || '',
  };

  if (socialEl.dataset?.links) {
    try {
      const parsedLinks = JSON.parse(socialEl.dataset.links);
      socialLinks = { ...socialLinks, ...parsedLinks };
    } catch (e) { }
  }

  socialEl.querySelectorAll('a').forEach((link) => {
    const href = link.href?.toLowerCase() || '';
    const socialKey = link.dataset?.social;
    if (socialKey && link.href) {
      socialLinks[socialKey] = link.href;
    } else {
      if (href.includes('linkedin')) socialLinks.linkedin = link.href;
      else if (href.includes('facebook')) socialLinks.facebook = link.href;
      else if (href.includes('twitter') || href.includes('x.com')) socialLinks.twitter = link.href;
      else if (href.includes('instagram')) socialLinks.instagram = link.href;
      else if (href.includes('youtube')) socialLinks.youtube = link.href;
    }
  });

  const orderStr = socialEl.dataset?.order || '';
  const order = orderStr
    ? orderStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    : ['linkedin', 'facebook', 'twitter', 'instagram', 'youtube'];

  const styles = extractStyles(socialEl);

  const iconColor =
    socialEl.dataset?.color ||
    socialEl.style?.getPropertyValue?.('--social-icon-color') ||
    '#EED191';

  const spacingAttr = socialEl.dataset?.spacing;
  let iconSpacing;
  if (spacingAttr === undefined || spacingAttr === null || spacingAttr === '') {
    iconSpacing = '';
  } else {
    const parsedSpacing = parseInt(spacingAttr, 10);
    iconSpacing = isNaN(parsedSpacing) ? '' : Math.min(50, Math.max(1, parsedSpacing));
  }

  return createCell(CELL_TYPES.SOCIAL, {
    x,
    y,
    socialLinks,
    socialOrder: order,
    iconColor,
    iconSpacing,
    styles: {
      textAlign: styles.textAlign || 'center',
    },
  });
};

const parseImage = (el, x, y) => {
  const img = el.tagName === 'IMG' ? el : el.querySelector('img');
  if (!img) return null;
  const styles = extractStyles(el);

  let redirectUrl = '';
  const parentAnchor = img.closest('a');
  if (parentAnchor) {
    redirectUrl = parentAnchor.getAttribute('href') || '';
  }

  return createCell(CELL_TYPES.IMAGE, {
    x,
    y,
    imageUrl: img.src || '',
    imageAlt: img.alt || '',
    redirectUrl,
    width: parseInt(img.width, 10) || parseInt(img.style?.width, 10) || 200,
    height: parseInt(img.height, 10) || parseInt(img.style?.height, 10) || 150,
    styles: {
      ...styles,
      textAlign: styles.textAlign || 'left',
    },
  });
};

const isLoopElement = (el) => {
  if (el.dataset?.comp === 'loop') return true;
  const firstChild = el.firstElementChild;
  if (firstChild?.dataset?.comp === 'loop') return true;
  return false;
};

const parseLoop = (el, x, y) => {
  const loopEl = el.dataset?.comp === 'loop' ? el : (el.firstElementChild?.dataset?.comp === 'loop' ? el.firstElementChild : el);
  let conditions = [];
  const condAttr = loopEl.getAttribute?.('data-loop-conditions') || loopEl.dataset?.loopConditions;
  if (condAttr) {
    try { conditions = JSON.parse(condAttr); } catch (e) { /* ignore */ }
  }
  if (conditions.length === 0) {
    // Try to parse from text content
    const text = loopEl.textContent || '';
    const regex = /\{\{(if|range)\s+\.([^}]*)\}\}|\{\{end\}\}/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match[0] === '{{end}}') {
        conditions.push({ id: uid(), type: 'end', value: '' });
      } else {
        conditions.push({ id: uid(), type: match[1], value: match[2] || '' });
      }
    }
  }
  return createCell(CELL_TYPES.LOOP, {
    x, y,
    width: 400,
    height: Math.max(50, conditions.length * 35 + 10),
    loopConditions: conditions,
  });
};

const parseText = (el, x, y) => {
  let html = el?.innerHTML || el?.textContent || '';

  html = html
    ?.replace(/^[\s\n\r]+/, '')
    ?.replace(/[\s\n\r]+$/, '')
    ?.replace(/^(<br\s*\/?>)+/gi, '')
    ?.replace(/(<br\s*\/?>)+$/gi, '');

  const styles = extractStyles(el);

  const lineHeight = parseInt(styles.lineHeight, 10) || 20;
  const lines = Math.max(1, Math.ceil(html.length / 50));
  const estimatedHeight = Math.max(60, lines * lineHeight + 20);

  return createCell(CELL_TYPES.TEXT, {
    x,
    y,
    content: html,
    width: parseInt(styles.width, 10) || 400,
    height: parseInt(styles.height, 10) || estimatedHeight,
    styles: {
      ...styles,
      padding: styles.padding || '8px',
    },
  });
};

const parseTableLayout = (container) => {
  const cells = [];
  let currentY = 20;
  const leftMargin = 20;

  // Only select direct child rows, not rows from nested tables
  const rows = container.querySelectorAll(':scope > tbody > tr, :scope > tr');
  if (rows.length === 0) return cells;

  rows.forEach((row) => {
    let currentX = leftMargin;
    let maxHeight = 0;

    const tds = row.querySelectorAll(':scope > td, :scope > th');

    tds.forEach((td) => {
      const content = td.innerHTML?.trim();

      if (!content || content === '<br>' || content === '<br/>' || content === '&nbsp;') {
        const width = parseInt(td.style.width, 10) || 100;
        currentX += width;
        return;
      }

      // Check for nested table (component wrapper)
      const nestedTable = td.querySelector('table');
      if (nestedTable) {
        const nestedTd = nestedTable.querySelector('td');
        if (nestedTd) {
          let cell = null;

          if (isLoopElement(nestedTable) || isLoopElement(nestedTd)) {
            cell = parseLoop(nestedTable, currentX, currentY);
          } else if (isButtonElement(nestedTd)) {
            cell = parseButton(nestedTd, currentX, currentY);
          } else if (isDividerElement(nestedTd)) {
            cell = parseDivider(nestedTd, currentX, currentY);
          } else if (isSocialElement(nestedTd)) {
            cell = parseSocial(nestedTd, currentX, currentY);
          } else if (isImageElement(nestedTd)) {
            cell = parseImage(nestedTd, currentX, currentY);
          } else if (nestedTd.innerHTML?.trim()) {
            cell = parseText(nestedTd, currentX, currentY);
          }

          if (cell) {
            cells.push(cell);
            currentX += cell.width + 20;
            maxHeight = Math.max(maxHeight, cell.height);
          }
          return;
        }
      }

      // Direct td content (no nested table)
      let cell = null;

      if (isLoopElement(td)) {
        cell = parseLoop(td, currentX, currentY);
      } else if (isButtonElement(td)) {
        cell = parseButton(td, currentX, currentY);
      } else if (isDividerElement(td)) {
        cell = parseDivider(td, currentX, currentY);
      } else if (isSocialElement(td)) {
        cell = parseSocial(td, currentX, currentY);
      } else if (isImageElement(td)) {
        cell = parseImage(td, currentX, currentY);
      } else {
        cell = parseText(td, currentX, currentY);
      }

      if (cell) {
        cells.push(cell);
        currentX += cell.width + 20;
        maxHeight = Math.max(maxHeight, cell.height);
      }
    });

    currentY += maxHeight + 20;
  });

  return cells;
};

const parseFreeformLayout = (container) => {
  const cells = [];
  let currentY = 20;
  const leftMargin = 20;

  const children = container.children;

  for (let i = 0; i < children.length; i++) {
    const el = children[i];
    const tagName = el.tagName?.toUpperCase();

    const content = el.innerHTML?.trim() || el.textContent?.trim();
    if (!content || content === '<br>' || content === '<br/>') continue;

    let cell = null;

    if (isButtonElement(el)) {
      cell = parseButton(el, leftMargin, currentY);
    } else if (isDividerElement(el) || tagName === 'HR') {
      cell = parseDivider(el, leftMargin, currentY);
    } else if (isSocialElement(el)) {
      cell = parseSocial(el, leftMargin, currentY);
    } else if (isImageElement(el) || tagName === 'IMG') {
      cell = parseImage(el, leftMargin, currentY);
    } else if (tagName === 'TABLE' && isLoopElement(el)) {
      cell = parseLoop(el, leftMargin, currentY);
    } else if (tagName === 'TABLE') {
      const tableCells = parseTableLayout(el);
      tableCells.forEach((tc) => {
        tc.y += currentY;
        cells.push(tc);
      });
      if (tableCells.length > 0) {
        const maxY = Math.max(...tableCells.map((c) => c.y + c.height));
        currentY = maxY + 20;
      }
      continue;
    } else {
      cell = parseText(el, leftMargin, currentY);
    }

    if (cell) {
      cells.push(cell);
      currentY += cell.height + 15;
    }
  }

  return cells;
};

const parsePixelValue = (value, defaultVal = 0) => {
  if (!value) return defaultVal;
  const num = parseFloat(String(value).replace('px', ''));
  return isNaN(num) ? defaultVal : Math.round(num);
};

const parseAbsoluteLayout = (container) => {
  const cells = [];

  const children = Array.from(container.children);

  for (let i = 0; i < children.length; i++) {
    const el = children[i];

    const styles = extractStyles(el);

    if (styles.position !== 'absolute') continue;

    const x = parsePixelValue(styles.left, 0);
    const y = parsePixelValue(styles.top, 0);
    const width = parsePixelValue(styles.width, 200);
    const height = parsePixelValue(styles.height, 100);
    const zIndex = parseInt(styles.zIndex, 10) || 1;
    const backgroundColor = styles.backgroundColor || 'transparent';
    const borderRadius = styles.borderRadius || '0';

    let cell = null;

    if (isLoopElement(el)) {
      cell = parseLoop(el, x, y);
    } else if (isButtonElement(el)) {
      cell = parseButton(el, x, y);
    } else if (isDividerElement(el)) {
      cell = parseDivider(el, x, y);
    } else if (isSocialElement(el)) {
      cell = parseSocial(el, x, y);
    } else if (isImageElement(el)) {
      cell = parseImage(el, x, y);
    } else {
      cell = parseText(el, x, y);
    }

    if (cell) {
      cell.x = x;
      cell.y = y;
      cell.width = width;
      cell.height = height;
      cell.zIndex = zIndex;
      cell.allowOverlap = el.getAttribute('data-allow-overlap') === 'true';

      const isComponentCell =
        cell.type === 'button' || cell.type === 'divider' || cell.type === 'social' || cell.type === 'loop';
      if (!isComponentCell) {
        cell.styles = {
          ...cell.styles,
          backgroundColor,
          borderRadius,
        };
      }
      cells.push(cell);
    }
  }

  return cells;
};

const findAbsoluteLayoutContainer = (body) => {
  const divs = body.querySelectorAll('div');

  for (let i = 0; i < divs.length; i++) {
    const div = divs[i];
    const styles = extractStyles(div);

    if (styles.position === 'relative') {
      const children = div.children;
      for (let j = 0; j < children.length; j++) {
        const childStyles = extractStyles(children[j]);
        if (childStyles.position === 'absolute') {
          return div;
        }
      }
    }
  }

  return null;
};

export const htmlToCells = (html) => {
  if (!html || typeof html !== 'string') {
    return createEmptySchema();
  }

  let cleanedHtml = html?.replace(/>\s+</g, '><')?.replace(/^\s+/, '')?.replace(/\s+$/, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanedHtml, 'text/html');
  const body = doc.body;

  let bgColor = '#ffffff';
  const bgMatch = html.match(/background-color:\s*([^;}"']+)/i);
  if (bgMatch) {
    bgColor = bgMatch[1].trim();
  }

  let cells = [];

  const canvasTable = body.querySelector('[data-editor-canvas="true"]');
  if (canvasTable) {
    const allNodes = Array.from(canvasTable.querySelectorAll('[data-cell-id]'));
    const nodes = allNodes.filter((node) => {
      let parent = node.parentElement;
      while (parent && parent !== canvasTable) {
        if (parent.hasAttribute('data-cell-id')) return false;
        parent = parent.parentElement;
      }
      return true;
    });

    nodes.forEach((node) => {
      const id = node.getAttribute('data-cell-id');
      const type = node.getAttribute('data-type') || CELL_TYPES.TEXT;
      const x = parseInt(node.getAttribute('data-x'), 10) || 0;
      const y = parseInt(node.getAttribute('data-y'), 10) || 0;
      const width = parseInt(node.getAttribute('data-w'), 10) || 200;
      const height = parseInt(node.getAttribute('data-h'), 10) || 100;
      const zIndex = parseInt(node.getAttribute('data-z'), 10) || 1;
      const allowOverlap = node.getAttribute('data-allow-overlap') === 'true';

      let cell = null;
      if (type === CELL_TYPES.TABLE) {
        // Reconstruct TABLE cell from data attributes
        let tableModel = null;
        let childCellsData = [];
        try {
          const tmAttr = node.getAttribute('data-table-model');
          if (tmAttr) tableModel = JSON.parse(tmAttr);
        } catch (e) { /* ignore parse error */ }
        try {
          const ccAttr = node.getAttribute('data-child-cells');
          if (ccAttr) childCellsData = JSON.parse(ccAttr);
        } catch (e) { /* ignore parse error */ }

        cell = createCell(CELL_TYPES.TABLE, {
          id,
          x,
          y,
          width,
          height,
          zIndex,
          allowOverlap,
          tableModel,
        });
        cells.push(cell);

        // Reconstruct child component cells
        if (childCellsData && childCellsData.length > 0) {
          childCellsData.forEach((ch) => {
            const childCell = createCell(ch.type || CELL_TYPES.TEXT, {
              ...ch,
              id: ch.id || uid(),
            });
            cells.push(childCell);
          });
        }
        // Skip the normal cell push below
        cell = null;
      } else if (type === CELL_TYPES.LOOP) {
        cell = parseLoop(node, x, y);
      } else if (type === CELL_TYPES.BUTTON) {
        cell = parseButton(node, x, y);
      } else if (type === CELL_TYPES.DIVIDER) {
        cell = parseDivider(node, x, y);
      } else if (type === CELL_TYPES.SOCIAL) {
        cell = parseSocial(node, x, y);
      } else if (type === CELL_TYPES.IMAGE) {
        cell = parseImage(node, x, y);
      } else {
        cell = parseText(node, x, y);
      }

      if (cell) {
        cell.id = id;
        cell.x = x;
        cell.y = y;
        cell.width = width;
        cell.height = height;
        cell.zIndex = zIndex;
        cell.allowOverlap = allowOverlap;
        cells.push(cell);
      }
    });

    if (cells.length > 0) {
      const containerStyles = (td) => {
        if (!td || !td.style) return {};
        const computed = td.getAttribute('style') || '';
        return cssToStyles(computed);
      };
      const canvasStyles = containerStyles(canvasTable);
      const canvasWidth = parseInt(canvasStyles.width, 10) || 601;

      return {
        cells,
        canvasWidth,
        canvasHeight: Math.max(800, Math.max(...cells.map((c) => c.y + c.height + 50))),
        bgColor,
      };
    }
  }

  const absoluteContainer = findAbsoluteLayoutContainer(body);
  if (absoluteContainer) {
    cells = parseAbsoluteLayout(absoluteContainer);

    const containerStyles = extractStyles(absoluteContainer);
    const canvasWidth = parseInt(containerStyles.width, 10) || 600;
    const canvasHeight = parseInt(containerStyles.minHeight, 10) || 800;

    if (cells.length > 0) {
      let maxX = canvasWidth;
      let maxY = canvasHeight;
      cells.forEach((c) => {
        maxX = Math.max(maxX, c.x + c.width + 20);
        maxY = Math.max(maxY, c.y + c.height + 20);
      });

      return {
        cells,
        canvasWidth: Math.max(600, maxX),
        canvasHeight: Math.max(400, maxY),
        bgColor,
      };
    }
  }

  // Only parse top-level tables (not nested ones)
  const allTables = body.querySelectorAll('table');
  const topLevelTables = Array.from(allTables).filter((table) => {
    // Check if this table is nested inside another table
    let parent = table.parentElement;
    while (parent && parent !== body) {
      if (parent.tagName === 'TABLE') {
        return false; // This is a nested table, skip it
      }
      parent = parent.parentElement;
    }
    return true; // This is a top-level table
  });

  if (topLevelTables.length > 0) {
    topLevelTables.forEach((table) => {
      const tableCells = parseTableLayout(table);
      cells = cells.concat(tableCells);
    });
  } else {
    // Only use freeform parsing if no tables were found
    const freeformCells = parseFreeformLayout(body);
    cells = cells.concat(freeformCells);
  }

  const allCells = [...cells];

  let maxX = 600;
  let maxY = 800;
  allCells.forEach((c) => {
    maxX = Math.max(maxX, c.x + c.width + 50);
    maxY = Math.max(maxY, c.y + c.height + 50);
  });

  return {
    cells: allCells,
    canvasWidth: Math.max(600, maxX),
    canvasHeight: Math.max(800, maxY),
    bgColor,
  };
};

export const isEmptyHtml = (html) => {
  if (!html) return true;
  const cleaned = html
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .trim();
  return !cleaned;
};

export default htmlToCells;
